import { NavLink } from 'react-router-dom';
import { LayoutDashboard, FileText, Settings as SettingsIcon, Shield, Building2, Package, X, ShieldCheck, HelpCircle, Boxes, LifeBuoy, Ticket } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { LG_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { useT } from '@/lib/i18n';
import type { messages as MSG } from '@/lib/i18n.messages';
import { Logo } from './Logo';
import { NeuNeuWidget } from './NeuNeuAI/NeuNeuWidget';
import { cn } from '@/lib/utils';

type Key = keyof typeof MSG;

const clientLinks: { to: string; key: Key; icon: any; tourId?: string }[] = [
  { to: '/dashboard',  key: 'nav.dashboard',  icon: LayoutDashboard, tourId: 'nav-dashboard' },
  { to: '/toolkits',   key: 'nav.toolkits',   icon: Boxes,           tourId: 'nav-toolkits' },
  { to: '/documents',  key: 'nav.documents',  icon: FileText,        tourId: 'nav-documents' },
  { to: '/support',    key: 'nav.support',    icon: LifeBuoy,        tourId: 'nav-support' },
  { to: '/settings',   key: 'nav.settings',   icon: SettingsIcon,    tourId: 'nav-settings' },
  { to: '/help',       key: 'nav.help',       icon: HelpCircle,      tourId: 'nav-help' },
];

const adminLinks: { to: string; key: Key; icon: any }[] = [
  { to: '/admin',           key: 'nav.admin.home',     icon: Shield },
  { to: '/admin/orgs',      key: 'nav.admin.orgs',     icon: Building2 },
  { to: '/admin/globals',   key: 'nav.admin.globals',  icon: ShieldCheck },
  { to: '/admin/releases',  key: 'nav.admin.releases', icon: Package },
  { to: '/admin/tickets',   key: 'nav.admin.tickets',  icon: Ticket },
];

interface SidebarProps {
  /** Mobile drawer open state (controlled). Desktop ignores this. */
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { isGlobalAdmin } = useAuth();
  const { t } = useT();
  const isLgUp = useMediaQuery(LG_MEDIA_QUERY);

  const content = (showTourTargets: boolean, opts?: { showNeuNeu?: boolean; showClose?: boolean }) => (
    <>
      <div className="flex h-20 items-center justify-between border-b border-black/10 px-5 dark:border-border/30">
        <div className="flex min-w-0 items-center gap-1.5">
          <Logo variant="lockup" tone="auto" className="h-11 max-w-[220px]" />
          <span className="shrink-0 self-end pb-1 text-[0.65rem] font-medium tracking-wide text-ink-600 dark:text-muted-foreground">
            ({t('nav.brand.beta')})
          </span>
        </div>
        {/* Bouton fermer : uniquement dans le drawer mobile, jamais dans la barre desktop */}
        {opts?.showClose && onClose && (
          <button onClick={onClose} className="text-ink-700 hover:text-ink-900 dark:text-muted-foreground dark:hover:text-white" aria-label={t('common.close')}>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3 text-sm">
        <SectionLabel>{t('nav.section.client')}</SectionLabel>
        {clientLinks.map(l => (
          <Item
            key={l.to}
            to={l.to}
            label={t(l.key)}
            icon={l.icon}
            onNavigate={onClose}
            tourId={showTourTargets ? l.tourId : undefined}
          />
        ))}

        {isGlobalAdmin && (
          <>
            <SectionLabel className="mt-6">{t('nav.section.admin')}</SectionLabel>
            {adminLinks.map(l => <Item key={l.to} to={l.to} label={t(l.key)} icon={l.icon} onNavigate={onClose} />)}
          </>
        )}
      </nav>

      <div className="mt-auto border-t border-black/10 dark:border-border/30 dark:bg-card/30">
        {opts?.showNeuNeu && (
          <div className="flex flex-col items-center px-3 pt-3">
            <NeuNeuWidget placement="sidebar" />
          </div>
        )}
        <div className="p-4 text-xs text-ink-700 dark:text-muted-foreground">
          <div className="font-display tracking-wide text-ink-900 dark:text-foreground">Neural Machines Inc.</div>
          <div className="mt-1 dark:text-muted-foreground">© {new Date().getFullYear()} · {t('nav.brand.tagline')}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop: static sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-black/10 bg-[#D9D4D9] text-ink-900 dark:border-border/30 dark:bg-card dark:text-foreground lg:flex">
        {content(isLgUp, { showNeuNeu: true })}
      </aside>

      {/* Mobile: drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-[#D9D4D9] text-ink-900 shadow-2xl transition-transform dark:bg-card dark:text-foreground lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={!open}
      >
        {content(!isLgUp, { showClose: true })}
      </aside>
    </>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('px-3 pb-1.5 pt-2 text-[0.65rem] font-semibold uppercase tracking-widest text-ink-600 dark:text-muted-foreground', className)}>
      {children}
    </div>
  );
}

function Item({ to, label, icon: Icon, onNavigate, tourId }: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; onNavigate?: () => void; tourId?: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard' || to === '/admin'}
      onClick={onNavigate}
      data-tour={tourId}
      className={({ isActive }) => cn(
        'group flex items-center gap-2.5 rounded-lg px-3 py-2 transition',
        isActive
          ? 'bg-brand-500/15 text-brand-700 ring-1 ring-inset ring-brand-500/20 dark:bg-primary/10 dark:text-brand-300 dark:ring-primary/20'
          : 'text-ink-800 hover:bg-black/5 hover:text-ink-900 dark:text-foreground/70 dark:hover:bg-secondary dark:hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </NavLink>
  );
}
